import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    BlockRounded,
    CardGiftcardRounded,
    CloseRounded,
} from "@mui/icons-material";
import { toPriceWithComma } from "../../../helper/utils";
import { CreditUsageCategoryEnum } from "../../../helper/enums/CreditUsageCategoryEnum";

const _AllocationConfirmDialog = ({
    open,
    onClose,
    onSubmit,
    submitting,
    formData,
    recipientLabel,
    rawCreditAmount,
    recipientCount,
    estimatedTotal,
    usageCategories,
    blockedSportsObjects,
}) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={() => !submitting && onClose()}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    p: 1,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            bgcolor: 'rgba(45, 107, 226, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'primary.main',
                        }}
                    >
                        <CardGiftcardRounded />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        تایید نهایی تخصیص اعتبار
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                >
                    <CloseRounded fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5, lineHeight: 1.8 }}>
                    آیا از صدور و تخصیص اعتبار با مشخصات زیر اطمینان دارید؟
                </Typography>

                <Box
                    sx={{
                        p: 2.5,
                        borderRadius: 3,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">عنوان اعتبار:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{formData.Name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">دریافت‌کنندگان:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main' }}>{recipientLabel}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">اعتبار هر فرد:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{toPriceWithComma(rawCreditAmount)} تومان</Typography>
                    </Box>
                    {recipientCount !== null && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">تعداد افراد:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{recipientCount} نفر</Typography>
                        </Box>
                    )}
                    {estimatedTotal !== null && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>کل بودجه تخصیص‌یافته:</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: 'success.main' }}>
                                {toPriceWithComma(estimatedTotal)} تومان
                            </Typography>
                        </Box>
                    )}

                    {/* Usage Categories Summary */}
                    <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                            بخش‌های مجاز مصرف:
                        </Typography>
                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            {usageCategories.map(code => (
                                <Chip
                                    key={code}
                                    label={CreditUsageCategoryEnum[code]?.label || code}
                                    size="small"
                                    color="primary"
                                    variant="filled"
                                    sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* Blocked Sports Summary */}
                    {usageCategories.includes("SPORT") && (
                        <Box sx={{ pt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.8 }}>
                                محدودیت ورزش‌ها:
                            </Typography>
                            {blockedSportsObjects.length > 0 ? (
                                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                                    {blockedSportsObjects.map(sport => (
                                        <Chip
                                            key={sport.Id}
                                            icon={<BlockRounded sx={{ fontSize: '13px !important' }} />}
                                            label={`ممنوع: ${sport.Name}`}
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.82rem' }}>
                                    بدون محدودیت (تمامی ورزش‌ها مجاز هستند)
                                </Typography>
                            )}
                        </Box>
                    )}

                    {formData.ExpireDate && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">مهلت استفاده (تاریخ انقضا):</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {new Date(formData.ExpireDate).toLocaleDateString('fa-IR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <Button
                    variant="text"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ borderRadius: 2.5, px: 2.5, color: 'text.secondary' }}
                >
                    انصراف
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    disabled={submitting}
                    sx={{
                        borderRadius: 2.5,
                        px: 3.5,
                        py: 1,
                        fontWeight: 800,
                        boxShadow: '0 4px 14px rgba(45, 107, 226, 0.3)',
                    }}
                >
                    {submitting ? <CircularProgress size={22} color="inherit" /> : "تایید و تخصیص اعتبار"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default _AllocationConfirmDialog;
